import { Button } from "@hub/components/ui/button";
import { Input } from "@hub/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@hub/components/ui/select";
import { Textarea } from "@hub/components/ui/textarea";
import type { OrgGlossaryEntry, OrgTeam } from "@hub/lib/org-types";
import { BOTS, INTEGRATIONS } from "@hub/lib/registry";
import { useRef } from "react";

function readList<T>(text: string, guard: (item: unknown) => item is T): T[] | null {
  try {
    const value: unknown = JSON.parse(text);
    return Array.isArray(value) && value.every(guard) ? value : null;
  } catch {
    return null;
  }
}
function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isTeam(value: unknown): value is OrgTeam {
  return (
    record(value) &&
    typeof value.id === "string" &&
    typeof value.label === "string" &&
    (value.cardLabel === null || typeof value.cardLabel === "string") &&
    typeof value.description === "string" &&
    Array.isArray(value.suggestedConnections) &&
    value.suggestedConnections.every((item: unknown) => typeof item === "string") &&
    (value.landingBot === null || typeof value.landingBot === "string")
  );
}
function isGlossary(value: unknown): value is OrgGlossaryEntry {
  return record(value) && typeof value.term === "string" && typeof value.meaning === "string";
}

export function TeamsEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const keys = useRef<string[]>([]);
  const teams = readList(value, isTeam);
  if (teams) while (keys.current.length < teams.length) keys.current.push(crypto.randomUUID());
  if (!teams)
    return (
      <p className="text-sm text-destructive">
        Teams could not be displayed. The saved data has an unexpected format.
      </p>
    );
  const update = (next: OrgTeam[]) => onChange(JSON.stringify(next, null, 2));
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Teams and roles</h3>
        <p className="text-sm text-muted-foreground">
          Select a team to edit its role and suggested tools. After adding a team, open it to fill
          in its details.
        </p>
      </div>
      {teams.map((team, index) => {
        const set = (patch: Partial<OrgTeam>) =>
          update(teams.map((item, i) => (i === index ? { ...item, ...patch } : item)));
        return (
          <details key={keys.current[index]} className="rounded-lg border p-4">
            <summary className="min-h-11 font-medium">
              {team.label || `New team ${index + 1}`}
            </summary>
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    keys.current.splice(index, 1);
                    update(teams.filter((_, i) => i !== index));
                  }}
                >
                  Remove team
                </Button>
              </div>
              <div className="grid gap-4 min-[901px]:grid-cols-2">
                <label htmlFor={`team-name-${index}`} className="space-y-1 text-sm">
                  <span className="block font-medium">Team name</span>
                  <Input
                    id={`team-name-${index}`}
                    value={team.label}
                    onChange={(e) => set({ label: e.target.value })}
                  />
                </label>
                <label htmlFor={`team-id-${index}`} className="space-y-1 text-sm">
                  <span className="block font-medium">Team ID</span>
                  <Input
                    id={`team-id-${index}`}
                    value={team.id}
                    onChange={(e) => set({ id: e.target.value })}
                  />
                  <span className="block text-xs text-muted-foreground">
                    Lowercase letters, numbers and underscores. Changing an existing ID may affect
                    people assigned to this team.
                  </span>
                </label>
                <label htmlFor={`team-card-${index}`} className="space-y-1 text-sm">
                  <span className="block font-medium">First-run card label</span>
                  <Input
                    id={`team-card-${index}`}
                    value={team.cardLabel ?? ""}
                    onChange={(e) => set({ cardLabel: e.target.value || null })}
                  />
                  <span className="block text-xs text-muted-foreground">
                    Leave blank to hide this team from the first-run choices.
                  </span>
                </label>
                <div className="space-y-1 text-sm">
                  <label htmlFor={`team-bot-${index}`} className="block font-medium">
                    Starting bot
                  </label>
                  <Select
                    value={team.landingBot ?? "none"}
                    onValueChange={(v) => set({ landingBot: v === "none" ? null : v })}
                  >
                    <SelectTrigger id={`team-bot-${index}`} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {BOTS.map((bot) => (
                        <SelectItem key={bot.id} value={bot.id}>
                          {bot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label htmlFor={`team-description-${index}`} className="block space-y-1 text-sm">
                <span className="block font-medium">Description</span>
                <Textarea
                  id={`team-description-${index}`}
                  value={team.description}
                  onChange={(e) => set({ description: e.target.value })}
                />
              </label>
              <div>
                <p className="mb-2 text-sm font-medium">Suggested connections</p>
                <div className="flex flex-wrap gap-2">
                  {INTEGRATIONS.map((item) => (
                    <Button
                      key={item.id}
                      type="button"
                      size="sm"
                      variant={team.suggestedConnections.includes(item.id) ? "default" : "outline"}
                      aria-pressed={team.suggestedConnections.includes(item.id)}
                      onClick={() =>
                        set({
                          suggestedConnections: team.suggestedConnections.includes(item.id)
                            ? team.suggestedConnections.filter((id) => id !== item.id)
                            : [...team.suggestedConnections, item.id],
                        })
                      }
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </details>
        );
      })}
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          update([
            ...teams,
            {
              id: "",
              label: "",
              cardLabel: null,
              description: "",
              suggestedConnections: [],
              landingBot: null,
            },
          ])
        }
      >
        Add team
      </Button>
    </div>
  );
}

export function GlossaryEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const keys = useRef<string[]>([]);
  const entries = readList(value, isGlossary);
  if (entries) while (keys.current.length < entries.length) keys.current.push(crypto.randomUUID());
  if (!entries)
    return (
      <p className="text-sm text-destructive">
        Glossary could not be displayed. The saved data has an unexpected format.
      </p>
    );
  const update = (next: OrgGlossaryEntry[]) => onChange(JSON.stringify(next, null, 2));
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Glossary</h3>
        <p className="text-sm text-muted-foreground">
          Explain company-specific words and abbreviations to the assistant.
        </p>
      </div>
      {entries.map((entry, index) => (
        <div
          key={keys.current[index]}
          className="grid gap-3 rounded-lg border p-4 min-[901px]:grid-cols-[1fr_2fr_auto]"
        >
          <label htmlFor={`glossary-term-${index}`} className="space-y-1 text-sm">
            <span className="block font-medium">Term</span>
            <Input
              id={`glossary-term-${index}`}
              value={entry.term}
              onChange={(e) =>
                update(
                  entries.map((item, i) =>
                    i === index ? { ...item, term: e.target.value } : item,
                  ),
                )
              }
            />
          </label>
          <label htmlFor={`glossary-meaning-${index}`} className="space-y-1 text-sm">
            <span className="block font-medium">Meaning</span>
            <Input
              id={`glossary-meaning-${index}`}
              value={entry.meaning}
              onChange={(e) =>
                update(
                  entries.map((item, i) =>
                    i === index ? { ...item, meaning: e.target.value } : item,
                  ),
                )
              }
            />
          </label>
          <Button
            type="button"
            variant="outline"
            className="self-end"
            onClick={() => {
              keys.current.splice(index, 1);
              update(entries.filter((_, i) => i !== index));
            }}
            aria-label={`Remove glossary entry ${entry.term || index + 1}`}
          >
            Remove
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() => update([...entries, { term: "", meaning: "" }])}
      >
        Add term
      </Button>
    </div>
  );
}
